import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function StudentHome() {
  const [student, setStudent] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    const { data: { user } } = await supabase.auth.getUser()
    const { data: acc } = await supabase.from('student_accounts')
      .select('*, students(*, groups(name, grade_level, day, time))').eq('id', user.id).single()
    if (!acc?.students) { setLoading(false); return }
    const s = acc.students; setStudent({ ...s, accountName: acc.name, accountPhone: acc.phone })

    const [{ data: grades }, { data: att }, { data: hw }] = await Promise.all([
      supabase.from('grades').select('*').eq('student_id', s.id),
      supabase.from('attendance').select('*').eq('student_id', s.id),
      supabase.from('homework').select('*').eq('student_id', s.id),
    ])

    const avgGrade = grades?.length ? Math.round(grades.reduce((a,g) => a+(g.score/g.total*100),0)/grades.length) : null
    const attPct = att?.length ? Math.round(att.filter(a=>a.status==='present').length/att.length*100) : null
    const hwPct = hw?.length ? Math.round(hw.filter(h=>h.status==='done').length/hw.length*100) : null
    const lastGrade = grades?.sort((a,b) => new Date(b.created_at)-new Date(a.created_at))[0]

    setStats({ avgGrade, attPct, hwPct, lastGrade, totalGrades: grades?.length, totalAtt: att?.length })
    setLoading(false)
  }

  const pctColor = (p) => p==null?'var(--text3)':p>=80?'var(--success)':p>=60?'var(--warning)':'var(--danger)'
  const pctClass = (p) => p==null?'':p>=80?'badge-success':p>=60?'badge-warning':'badge-danger'

  if (loading) return <div className="empty">Loading...</div>
  if (!student) return (
    <div className="empty">
      <div style={{ fontSize:48, marginBottom:12 }}>🎓</div>
      <div style={{ fontWeight:500 }}>Account not linked</div>
      <div style={{ color:'var(--text3)', marginTop:4 }}>Contact your teacher</div>
    </div>
  )

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Profile */}
      <div className="card" style={{ display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ width:60, height:60, borderRadius:'50%', background:'var(--accent-bg)', color:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:700 }}>
          {student.name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:19, fontWeight:600 }}>{student.name}</div>
          <div style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>🎓 Student</div>
          {student.grade && <span className="badge badge-accent" style={{ marginTop:6, display:'inline-flex' }}>{student.grade}</span>}
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:12, color:'var(--text3)' }}>Student Code</div>
          <div style={{ fontFamily:'monospace', fontWeight:700, color:'var(--accent)', fontSize:16, letterSpacing:1 }}>{student.student_code}</div>
        </div>
      </div>

      {/* Group info */}
      {student.groups && (
        <div className="card" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:28 }}>📅</span>
          <div>
            <div style={{ fontWeight:500 }}>{student.groups.name}</div>
            <div style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>
              {student.groups.day && <span>{student.groups.day}</span>}
              {student.groups.time && <span> · {student.groups.time}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:12 }}>
        {[
          { label:'Avg. Grade',   val: stats?.avgGrade!=null?stats.avgGrade+'%':'—', color: pctColor(stats?.avgGrade) },
          { label:'Attendance',   val: stats?.attPct!=null?stats.attPct+'%':'—',     color: pctColor(stats?.attPct)   },
          { label:'Homework',     val: stats?.hwPct!=null?stats.hwPct+'%':'—',       color: pctColor(stats?.hwPct)    },
          { label:'Total Exams',  val: stats?.totalGrades??0,                        color: 'var(--text)'             },
        ].map(stat => (
          <div key={stat.label} className="card" style={{ textAlign:'center', padding:'14px' }}>
            <div style={{ fontSize:12, color:'var(--text3)', marginBottom:6 }}>{stat.label}</div>
            <div style={{ fontSize:24, fontWeight:700, color: stat.color }}>{stat.val}</div>
          </div>
        ))}
      </div>

      {/* Latest Exam */}
      {stats?.lastGrade && (
        <div className="card">
          <div style={{ fontSize:13, color:'var(--text3)', marginBottom:10 }}>Latest Exam</div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontWeight:500 }}>{stats.lastGrade.exam_name}</div>
            <span className={`badge ${pctClass(Math.round(stats.lastGrade.score/stats.lastGrade.total*100))}`}>
              {stats.lastGrade.score}/{stats.lastGrade.total} — {Math.round(stats.lastGrade.score/stats.lastGrade.total*100)}%
            </span>
          </div>
          {stats.lastGrade.note && <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>{stats.lastGrade.note}</div>}
        </div>
      )}
    </div>
  )
}